import React from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { Sidebar } from './components/layout/Sidebar';
import { Header } from './components/layout/Header';
import { Dashboard } from './pages/Dashboard';
import { UploadROV } from './pages/UploadROV';
import { AnalysisResults } from './pages/AnalysisResults';
import { DocumentsList } from './pages/DocumentsList';
import { FeedbackLoop } from './pages/FeedbackLoop';
import { Settings } from './pages/Settings';
import { Login } from './pages/Login';
import { Logout } from './pages/Logout';
import { UploadModal } from './components/shared/NewTraceModal';
import { ToastContainer } from './components/shared/ToastContainer';

const viewConfig: Record<string, { title: string; subtitle: string }> = {
  dashboard: { title: 'Dashboard', subtitle: 'Overview of document analysis and compliance metrics' },
  upload: { title: 'Upload Document', subtitle: 'Upload documents for TRACE AI-powered compliance analysis' },
  analysis: { title: 'TRACE AI Analysis', subtitle: 'Detailed findings and compliance evaluation results' },
  documents: { title: 'Documents', subtitle: 'History of all uploaded and processed documents' },
  feedback: { title: 'Feedback Loop', subtitle: 'Continuous improvement of TRACE AI analysis accuracy' },
  settings: { title: 'Settings', subtitle: 'Configure analysis templates, thresholds, and integrations' },
};

const AppContent: React.FC = () => {
  const { activeView, sidebarCollapsed, authScreen, login, completeLogout, user } = useApp();

  // ── Login screen ─────────────────────────────────────────────
  if (authScreen === 'login') {
    return <Login onLogin={login} />;
  }

  // ── Logout screen ────────────────────────────────────────────
  if (authScreen === 'logout') {
    return (
      <Logout
        userName={user?.name || 'User'}
        onBackToLogin={completeLogout}
      />
    );
  }

  // ── Main App ─────────────────────────────────────────────────
  const config = viewConfig[activeView] || viewConfig.dashboard;

  const renderView = () => {
    switch (activeView) {
      case 'dashboard': return <Dashboard />;
      case 'upload': return <UploadROV />;
      case 'analysis': return <AnalysisResults />;
      case 'documents': return <DocumentsList />;
      case 'feedback': return <FeedbackLoop />;
      case 'settings': return <Settings />;
      default: return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-surface-50 bg-mesh-gradient">
      <Sidebar />
      <main
        className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-[72px]' : 'ml-[272px]'
          }`}
      >
        <Header title={config.title} subtitle={config.subtitle} />
        <div className="p-8">
          {renderView()}
        </div>
      </main>
      <UploadModal />
      <ToastContainer />
    </div>
  );
};

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;
